import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { KeyRound, Loader2, CheckCircle2 } from "lucide-react";
import { AfyanexLogo } from "@/components/AfyanexLogo";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function check(rules, v) {
  return rules.every((fn) => fn(v));
}

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const rules = [
    { label: "8+ characters", ok: pw.length >= 8 },
    { label: "Contains a letter", ok: /[a-zA-Z]/.test(pw) },
    { label: "Contains a number", ok: /\d/.test(pw) },
    { label: "Matches confirmation", ok: pw.length > 0 && pw === confirm },
  ];
  const allOk = rules.every((r) => r.ok);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!allOk) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      toast.success("Password updated — please sign in");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Reset failed — the link may have expired");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-clinical-bg p-6">
      <div className="card-surface w-full max-w-md p-8" data-testid="reset-password-page">
        <div className="flex items-center gap-2.5 mb-6">
          <AfyanexLogo markSize={28} />
          <span className="ml-1 pl-2.5 border-l border-clinical-border text-[10px] tracking-[0.14em] uppercase text-clinical-subtext font-semibold">
            Reset password
          </span>
        </div>

        {done ? (
          <div className="text-center py-6" data-testid="reset-success">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-600">
              <CheckCircle2 className="h-6 w-6" />
            </span>
            <h2 className="mt-4 font-heading text-xl font-semibold">Password updated</h2>
            <p className="mt-2 text-sm text-clinical-subtext">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-semibold tracking-tight">
              Choose a new password
            </h1>
            <p className="mt-1 text-sm text-clinical-subtext">
              This single-use link expires shortly. Pick something strong — you'll use this to
              log in.
            </p>

            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div>
                <label className="label-eyebrow mb-1.5 block">New password</label>
                <input
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="input-clinical"
                  autoComplete="new-password"
                  data-testid="new-password-input"
                  required
                />
              </div>
              <div>
                <label className="label-eyebrow mb-1.5 block">Confirm password</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="input-clinical"
                  autoComplete="new-password"
                  data-testid="confirm-password-input"
                  required
                />
              </div>

              <ul className="grid grid-cols-2 gap-1.5">
                {rules.map((r) => (
                  <li
                    key={r.label}
                    className={`text-xs flex items-center gap-1.5 ${
                      r.ok ? "text-emerald-600" : "text-clinical-subtext"
                    }`}
                  >
                    <span
                      className={`inline-block h-1.5 w-1.5 rounded-full ${
                        r.ok ? "bg-emerald-500" : "bg-clinical-border"
                      }`}
                    />
                    {r.label}
                  </li>
                ))}
              </ul>

              <button
                type="submit"
                disabled={!allOk || submitting}
                className="btn-primary w-full"
                data-testid="reset-submit-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Updating…
                  </>
                ) : (
                  <>
                    <KeyRound className="h-4 w-4" /> Update password
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
